import { useCallback, useEffect, useRef, useState } from 'react'

import {
  getConversationSummaryBatcher,
  subscribeToConversationChanges,
} from '../services/conversation-summary-batcher'
import type { ConversationSummary } from '../types'
import { useMessageComponents } from '../components/message-components-provider'
import { useMessageApi } from './use-message-api'

/**
 * Badge state for one entity, fetched in a batch with every other entity asking at the same moment.
 *
 * Prefer this over {@link useMessageCount} anywhere rows are rendered in a list: the count hook is one
 * HTTP call per row, this is one call per page. It also carries the per-reader unread state, which a
 * raw count cannot express.
 *
 * `summary` is `null` while loading and for an entity with no messages at all. A failed fetch keeps the
 * last value (or the initial `null`) and sets `error`, so a caller can distinguish "unknown" from "no
 * messages" instead of rendering every cell empty when one batch fails.
 *
 * The stored value carries the entity key it was fetched for, and it is surfaced only when that key
 * matches the CURRENT entity. A hook instance reused for another row (a re-sorted/virtualized grid, or
 * a panel re-pointed to another thread) therefore shows nothing for the new entity until its own data
 * arrives — the suppression happens in render, so there is no painted frame of the previous entity's
 * badge (a passive effect that cleared after the change would still let one frame through).
 */
export function useConversationSummary(entityTypeId: string, entityId: string) {
  const entityKey = `${entityTypeId}:${entityId}`

  const [summaryState, setSummaryState] = useState<{
    key: string
    value: ConversationSummary | null
    error: string | null
  }>({ key: entityKey, value: null, error: null })
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const apiService = useMessageApi()
  const { config } = useMessageComponents()
  const { userToken, apiEndpoint } = config

  // Guards against a response landing after the component has moved on to another entity or
  // unmounted — a grid re-sorts under a slow request often enough for this to matter.
  const requestRef = useRef(0)

  const load = useCallback(async () => {
    const key = `${entityTypeId}:${entityId}`
    // Empty ids are how a caller opts out (see EntityChatTrigger's summaryMode 'off'). Without this
    // guard that opt-out would still fire a batched call with no entity type and take a 400.
    if (!userToken || !entityTypeId || !entityId) {
      setSummaryState({ key, value: null, error: null })
      return
    }

    const requestId = ++requestRef.current
    setIsLoading(true)

    try {
      const result = await getConversationSummaryBatcher(apiEndpoint, apiService).fetch(
        entityTypeId,
        entityId
      )
      if (requestRef.current !== requestId) return
      setSummaryState({ key, value: result, error: null })
    } catch (err) {
      if (requestRef.current !== requestId) return
      // A failed batch must read as UNKNOWN, not a positive "no messages": keep the last value for THIS
      // entity (so the cell does not blank when one summaries call 500s/403s) and expose `error`. A
      // failure while loading a DIFFERENT entity has no prior value to keep, so it starts from null.
      const message = err instanceof Error ? err.message : 'Failed to fetch conversation summary'
      setSummaryState((prev) => ({ key, value: prev.key === key ? prev.value : null, error: message }))
    } finally {
      if (requestRef.current === requestId) {
        setIsLoading(false)
      }
    }
  }, [entityTypeId, entityId, userToken, apiEndpoint, apiService])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(
    () => () => {
      // Stop a late response from writing into an unmounted component.
      requestRef.current++
    },
    []
  )

  // Re-read when something else in this tab changes this entity — a bulk mark read/unread, or a
  // per-message toggle in the panel. Every hook notified in the same tick shares one request.
  useEffect(
    () =>
      subscribeToConversationChanges((changedTypeId, changedEntityId) => {
        if (changedTypeId === entityTypeId && changedEntityId === entityId) {
          void load()
        }
      }),
    [entityTypeId, entityId, load]
  )

  /**
   * Apply a known change locally instead of re-reading the page.
   *
   * Marking a thread read has one exact, predictable effect on the badge, so refetching every row's
   * summary to discover it is a round trip that buys nothing. Only for changes this client just
   * made — anything caused by somebody else still needs {@link refresh}.
   */
  const updateSummary = useCallback(
    (updater: (current: ConversationSummary) => ConversationSummary) => {
      setSummaryState((prev) => (prev.value ? { ...prev, value: updater(prev.value) } : prev))
    },
    []
  )

  // Surface data only when it belongs to the entity currently asked for. Suppressing a mismatch in
  // render (rather than clearing it in an effect) is what closes the one-frame flash.
  const summary = summaryState.key === entityKey ? summaryState.value : null
  const error = summaryState.key === entityKey ? summaryState.error : null

  return {
    /** Null while loading, and for an entity with no messages. */
    summary,
    isLoading,
    error,
    /** Re-fetch. For changes made elsewhere — a poll tick, or another user's message. */
    refresh: load,
    updateSummary,
  }
}
